import { H3, Text } from '@/components/atoms'
import { House03Icon } from '@/components/atoms/icons/navigation'
import { useTranslation } from 'react-i18next'
import { usePolicyCard } from './policy-card.hook'

type ArrearsDisplay = {
  days: number
  amount: string
}

export type PolicyCardProps = {
  id?: string
  displayName: string
  displayAmount?: string
  displayIntervalKey?: string
  isInArrears: boolean
  arrearsDisplay?: ArrearsDisplay
  nextBillingDate?: string
  policyType?: 'pet' | 'renters'
  pet?: { petPictureUrl?: string; type?: string }
}

export function PolicyCard({ policy }: { policy: PolicyCardProps }) {
  const { t } = useTranslation()
  const { petIcon } = usePolicyCard(policy.pet)

  const isRenters = policy.policyType === 'renters'

  return (
    <div className="flex w-full min-w-75.5 flex-col items-start gap-6 rounded-lg border border-neutral-100 bg-white px-6 py-8 md:max-w-2xs">
      <div className="flex items-center gap-2">
        {isRenters ? (
          <House03Icon className="h-8 w-8" />
        ) : policy.pet?.petPictureUrl ? (
          <img
            src={policy.pet.petPictureUrl}
            alt={policy.displayName}
            className="h-8 w-8 rounded-full object-cover shadow-sm"
          />
        ) : (
          <img
            src={petIcon}
            alt={policy.pet?.type ?? 'pet'}
            className="h-8 w-8"
          />
        )}
        <H3 className="text-2xl! font-bold text-indigo-900!">
          {policy.displayName}
        </H3>
      </div>

      <div className="flex flex-col gap-2">
        <Text className="text-xl! text-neutral-900">
          {policy.displayIntervalKey
            ? t(policy.displayIntervalKey)
            : t('payment.policies.payment')}
        </Text>
        {policy.displayAmount && (
          <Text className="text-indigo-base text-xl! font-bold">
            {policy.displayAmount}
          </Text>
        )}
      </div>

      {policy.isInArrears && policy.arrearsDisplay && (
        <Text className="text-sm! text-red-600">
          {t('payment.policies.inArrears', {
            days: policy.arrearsDisplay.days,
            amount: policy.arrearsDisplay.amount,
          })}
        </Text>
      )}

      {policy.nextBillingDate && (
        <>
          <hr className="w-full border-neutral-100" />
          <Text className="text-sm! text-neutral-700">
            {t('payment.policies.nextBilling', {
              date: policy.nextBillingDate,
            })}
          </Text>
        </>
      )}
    </div>
  )
}
